import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { ProfileContext } from "@velaro/velaro-component-lib";
import { Modal, ModalShell, SectionCard, PrimaryButton, SecondaryButton, GhostButton, IconButton, Divider } from "@/components/ds";
import { ArrowRight, Check, X } from "@/components/ds/icons";
import MoshkyMark from "@/components/MoshkyMark";
import { TOUR_STEPS, TourFacts } from "./tourSteps";
import useShopifyTourFacts from "./useShopifyTourFacts";
import useTourState, { TourState } from "./useTourState";

/**
 * The Moshky Shopify welcome tour.
 *
 * WIZARD DESIGN STANDARD (CLAUDE.md) — this is a walkthrough, not a config wizard, but the
 * same "don't trap the user" contract applies and is implemented here:
 *  1. Re-opening NEVER silently forces one path. If there's prior progress the first screen is
 *     an explicit choice: Resume / Start from the beginning / Explore on my own.
 *  2. Upsert-only, non-destructive. The tour reads; it changes no product configuration. "Start
 *     from the beginning" resets only this person's progress pointer, and the server snapshots
 *     the prior pointer before clearing it.
 *  3. Every state change is audit-logged server-side (who, when, old -> new) by
 *     OnboardingTourController.
 *  4. Dismissing is not an escape hatch that loses the content — the Moshky launcher on the
 *     home page reopens it forever.
 *
 * Accessibility: rendered through ds/ <Modal>, which delegates to @headlessui/react <Dialog>
 * (focus trap, Esc, aria wiring). Step changes announce through an aria-live region.
 */

interface Props {
  onClose: () => void;
  /** Injected by the entry point so both it and this component share one state hook instance. */
  tour: ReturnType<typeof useTourState>;
}

type Screen = "choice" | "welcome" | "steps";

function StepBody({
  facts,
  index,
  factsLoading
}: {
  facts: TourFacts;
  index: number;
  factsLoading: boolean;
}) {
  const step = TOUR_STEPS[index];
  const fact = step.fact(facts);
  const location = useLocation();
  // The tour lives in the app shell, so it stays mounted while the person follows a stop's link.
  // When they land on the stop's destination, say so instead of offering the link again. Not
  // evaluated while facts are loading (a stop's destination can depend on them).
  const [targetPath, targetQuery = ""] = step.to(facts).split("?");
  const wanted = new URLSearchParams(targetQuery);
  const have = new URLSearchParams(location.search);
  const onTargetScreen =
    !factsLoading &&
    !!matchPath({ path: targetPath, end: true }, location.pathname) &&
    Array.from(wanted.entries()).every(([k, v]) => have.get(k) === v);
  return (
    <div>
      {/* Only the position + title are announced on a step change. Putting aria-live on the whole
          panel made a screen reader re-read every paragraph and link on each Next. */}
      <div aria-live="polite">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Stop {index + 1} of {TOUR_STEPS.length}
        </p>
        <h3 className="text-lg font-semibold text-zinc-800 mt-1">{step.title}</h3>
      </div>
      <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{step.body}</p>
      {fact && (
        <p className="text-sm text-zinc-800 mt-3 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2">
          {fact}
        </p>
      )}
      <div className="mt-4">
        {/* Persistent live region: its text swaps, so the transition is announced. */}
        <p
          role="status"
          aria-live="polite"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 empty:hidden"
        >
          {onTargetScreen && (
            <>
              <Check className="w-4 h-4" />
              You're on this screen now. Look around, then press Next.
            </>
          )}
        </p>
        {!onTargetScreen && (
          <Link
            to={step.to(facts)}
            className="inline-flex items-center gap-1.5 text-(--ds-primary) text-sm font-medium hover:underline"
          >
            {step.linkLabel}
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

function ProgressRail({
  currentIndex,
  onJump
}: {
  currentIndex: number;
  onJump: (i: number) => void;
}) {
  return (
    <ol className="space-y-1">
      {TOUR_STEPS.map((step, i) => {
        const done = i < currentIndex;
        const current = i === currentIndex;
        return (
          <li key={step.key}>
            <button
              type="button"
              onClick={() => onJump(i)}
              aria-current={current ? "step" : undefined}
              className={`w-full flex items-center gap-2 text-left text-xs rounded-lg px-2 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-(--ds-primary) ${
                current
                  ? "bg-(--ds-primary)/10 text-zinc-800 font-semibold"
                  : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              <span
                className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                  done
                    ? "bg-green-100 text-green-700"
                    : current
                      ? "bg-(--ds-primary) text-white"
                      : "bg-zinc-100 text-zinc-600"
                }`}
              >
                {done ? <Check className="w-3 h-3" /> : i + 1}
              </span>
              <span className="truncate">{step.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export default function ShopifyWelcomeTour({ onClose, tour }: Props) {
  const { state, saveError, recordProgress, recordComplete, recordDismiss, recordReset } =
    tour;
  const { facts, loading: factsLoading } = useShopifyTourFacts(true);
  const panelRef = React.useRef<HTMLElement | null>(null);
  const location = useLocation();
  const { profile } = useContext(ProfileContext);
  // Real signed-in profile name, same source the home page greeting uses. Never a placeholder:
  // with no first name the greeting simply drops it rather than inventing one.
  const firstName = profile?.firstName || profile?.displayName?.split(" ")[0] || "";

  const saved: TourState | null = state;
  const hasPriorProgress = useMemo(
    () =>
      !!saved &&
      saved.completedAt === null &&
      (saved.currentStepIndex > 0 || saved.startedAt !== null),
    [saved]
  );

  // Re-entrancy: prior, unfinished progress gets an explicit choice instead of being silently
  // resumed OR silently restarted. A finished tour replays from the welcome screen.
  const [screen, setScreen] = useState<Screen>(hasPriorProgress ? "choice" : "welcome");
  // stepIndex is 0-based over TOUR_STEPS; the persisted pointer is 1-based (0 = welcome only).
  const [stepIndex, setStepIndex] = useState(0);
  // Set when the person picked "Start from the beginning" but hasn't committed to it yet — the
  // reset only fires once they actually start the tour again.
  const [restartPending, setRestartPending] = useState(false);

  // The Resume/Start-over choice is decided ONCE, from whatever progress existed when the tour
  // opened. It must never re-fire on later state changes: recordProgress() updates `state` on
  // every "Next", which would otherwise make hasPriorProgress flip true mid-tour and yank the
  // person back to the choice screen after their first step. The ref latches the decision.
  const choiceSettled = React.useRef(false);
  useEffect(() => {
    if (choiceSettled.current) return;
    if (!saved) return; // state still loading — decide after the first real read
    choiceSettled.current = true;
    if (hasPriorProgress) setScreen("choice");
  }, [saved, hasPriorProgress]);

  /**
   * Starts (or restarts) the tour at `index`.
   *
   * `restartFirst` awaits a reset before the first progress write. Two reasons it must be
   * awaited rather than fired alongside: (1) server-side progress is monotonic, so replaying a
   * finished tour without clearing the pointer would leave "resume" pinned at the old last stop,
   * and (2) an unawaited reset racing the progress POST could land second and wipe the new
   * progress.
   */
  async function beginAt(index: number, restartFirst = false) {
    setStepIndex(index);
    setScreen("steps");
    if (restartFirst) await recordReset();
    await recordProgress(index + 1);
  }

  function goTo(index: number) {
    if (index < 0) {
      setScreen("welcome");
      return;
    }
    if (index >= TOUR_STEPS.length) {
      recordComplete();
      onClose();
      return;
    }
    setStepIndex(index);
    recordProgress(index + 1);
  }

  function dismiss() {
    recordDismiss();
    onClose();
  }

  const resumeIndex = Math.min(
    Math.max((saved?.currentStepIndex ?? 1) - 1, 0),
    TOUR_STEPS.length - 1
  );

  const header = (
    <div className="flex items-start justify-between gap-3 px-6 pt-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-(--ds-primary) flex items-center justify-center shrink-0">
          <MoshkyMark width={22} fill="white" />
        </div>
        <div>
          <p className="text-sm font-semibold text-zinc-800">Moshky</p>
          <p className="text-xs text-zinc-600">Your Velaro guide</p>
        </div>
      </div>
      <IconButton aria-label="Close the tour" onClick={dismiss}>
        <X className="w-5 h-5" />
      </IconButton>
    </div>
  );

  // "I'll keep your place" is a promise — if the save is failing, say so rather than letting the
  // person find out by losing their place. Never blocks the tour.
  const saveErrorBanner = saveError ? (
    <p
      role="status"
      className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3"
    >
      Couldn't save your place ({saveError}). You can keep going — the tour just may greet you
      again next time.
    </p>
  ) : null;

  // Move focus into the docked panel when the walkthrough starts and when a followed link lands
  // on the stop's screen (the clicked link unmounts, which would otherwise drop focus to body).
  useEffect(() => {
    if (screen === "steps") panelRef.current?.focus();
  }, [screen, location.pathname, location.search]);

  // The walkthrough itself is a NON-modal docked panel: the point of a stop is to go look at the
  // real screen, so it must neither block the page nor die on navigation. Its host lives in
  // MainLayout (above the routed outlet), so a route change leaves it mounted.
  if (screen === "steps") {
    return (
      <aside
        ref={panelRef}
        tabIndex={-1}
        aria-label="Velaro setup tour with Moshky"
        data-testid="tour-docked-panel"
        className="fixed z-9998 bottom-24 right-4 sm:right-6 w-[26rem] max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-(--ds-primary)"
      >
        <SectionCard className="!p-0 shadow-2xl">
        {header}
        <div className="px-6 pb-6 pt-4">
          {saveErrorBanner}
          <nav aria-label="Tour stops" className="mb-4">
            <ProgressRail currentIndex={stepIndex} onJump={goTo} />
          </nav>
          <StepBody facts={facts} index={stepIndex} factsLoading={factsLoading} />
          <Divider />
          <div className="flex flex-wrap items-center gap-2">
            <SecondaryButton onClick={() => goTo(stepIndex - 1)}>
              {stepIndex === 0 ? "Back to start" : "Back"}
            </SecondaryButton>
            <PrimaryButton onClick={() => goTo(stepIndex + 1)}>
              {stepIndex === TOUR_STEPS.length - 1 ? "Finish" : "Next"}
            </PrimaryButton>
            <GhostButton onClick={dismiss}>Explore on my own</GhostButton>
          </div>
        </div>
        </SectionCard>
      </aside>
    );
  }

  return (
    <Modal open onClose={dismiss} size="2xl" aria-label="Velaro setup tour with Moshky">
      <ModalShell.Body className="!p-0">
        {header}
        <div className="px-6 pb-6 pt-4">
          {saveErrorBanner}
          {screen === "choice" && (
            <div>
              <h2 className="text-lg font-semibold text-zinc-800">
                Pick up where you left off?
              </h2>
              <p className="text-sm text-zinc-600 mt-2">
                You've already started the tour. Nothing you've set up changes either way.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <PrimaryButton onClick={() => beginAt(resumeIndex)}>
                  Resume at stop {resumeIndex + 1}
                </PrimaryButton>
                <SecondaryButton
                  onClick={() => {
                    // The reset is deliberately NOT sent here — it happens when they actually
                    // commit by clicking "Show me around" on the welcome screen (beginAt's
                    // restartFirst). Sending it now would throw away their place for someone who
                    // just wanted to re-read the intro and then resume.
                    setRestartPending(true);
                    setScreen("welcome");
                  }}
                >
                  Start from the beginning
                </SecondaryButton>
                <GhostButton onClick={dismiss}>Explore on my own</GhostButton>
              </div>
            </div>
          )}

          {screen === "welcome" && (
            <div>
              <h2 className="text-lg font-semibold text-zinc-800">
                {firstName ? `Hi ${firstName}, I'm Moshky.` : "Hi, I'm Moshky."}
              </h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Your Shopify store set up a Velaro site on its own, so a few things are already
                running. Let me show you the {TOUR_STEPS.length} screens that matter, in the
                order they matter.
              </p>
              <ol className="mt-4 space-y-1.5">
                {TOUR_STEPS.map((step, i) => (
                  <li key={step.key} className="flex items-start gap-2 text-sm text-zinc-700">
                    <span className="w-5 h-5 shrink-0 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-semibold flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    {step.title}
                  </li>
                ))}
              </ol>
              <Divider />
              <div className="flex flex-wrap items-center gap-2">
                <PrimaryButton
                  onClick={() => beginAt(0, restartPending || !!saved?.completedAt)}
                >
                  Show me around
                </PrimaryButton>
                <GhostButton onClick={dismiss}>Explore on my own</GhostButton>
              </div>
              <p className="text-xs text-zinc-600 mt-3">
                Leave whenever you like — I'll keep your place, and you can reopen this from the
                home page any time.
              </p>
            </div>
          )}

        </div>
      </ModalShell.Body>
    </Modal>
  );
}
